import React from 'react';
import { checkPassword } from '../../utils/passwordRule';

const REQUIREMENTS = [
  { key: 'lengthOk', label: 'At least 8 characters' },
  { key: 'hasUpper', label: 'An uppercase letter' },
  { key: 'hasLower', label: 'A lowercase letter' },
  { key: 'hasDigit', label: 'A number' },
];

// Shared with the password input's aria-describedby (Signup, ResetPassword). Only
// one checklist is ever mounted per page, so a fixed id is safe.
export const PASSWORD_REQUIREMENTS_ID = 'password-requirements';

/**
 * Live checklist of the password requirements. Renders under a new-password
 * field so the user can see which requirements are met while typing.
 *
 * Accessible: the list re-announces via aria-live. Met items get a green check
 * mark; unmet items keep the neutral marker. The met/unmet word is visually
 * hidden (sr-only) rather than shown as text. Each <li> is aria-atomic so a
 * status change announces the whole "label, status" phrase, not just the
 * changed word.
 */
const PasswordRequirements = ({ password }) => {
  const result = checkPassword(password);
  const tooLong = typeof password === 'string' && password.length > 80;

  return (
    <ul
      id={PASSWORD_REQUIREMENTS_ID}
      aria-live="polite"
      className="text-sm space-y-1 text-text-muted"
    >
      {REQUIREMENTS.map(({ key, label }) => {
        const met = result[key];
        const displayLabel = key === 'lengthOk' && tooLong ? '80 characters maximum' : label;
        return (
          <li
            key={key}
            aria-atomic="true"
            className={met ? 'text-green-400' : 'text-text-muted'}
          >
            <span aria-hidden="true">{met ? '✓' : '•'}</span>{' '}
            {displayLabel}
            <span className="sr-only">, {met ? 'met' : 'not met yet'}</span>
          </li>
        );
      })}
    </ul>
  );
};

export default PasswordRequirements;
